import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    collection,
    addDoc,
    doc,
    getDoc,
    updateDoc,
    getDocs,
    query,
    orderBy,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
    ChevronLeft,
    Save,
    X,
    DollarSign,
    Calendar,
    MapPin,
    FileText,
    AlertCircle,
    CheckCircle,
    Loader2,
    Building2,
    Plus,
    Edit3,
    Camera,
    TrendingUp,
    Tag,
    Briefcase,
    Users,
    Clock,
} from "lucide-react";

const XFinanceIncomeForm = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [categories, setCategories] = useState([]);

    const [formData, setFormData] = useState({
        descripcion: "",
        categoria: "",
        cantidad: "",
        fecha: new Date().toISOString().split('T')[0],
        fuente: "",
        metodoCobro: "",
        ubicacion: "",
        notas: "",
        esRecurrente: false,
        frecuenciaRecurrencia: "mensual",
        fechaFin: "",
        etiquetas: [],
        comprobanteUrl: "",
        cliente: "",
        proyecto: "",
        tipoIngreso: "unico",
        horasTrabajadas: "",
        tarifaPorHora: "",
    });

    const [comprobanteFile, setComprobanteFile] = useState(null);
    const [comprobantePreview, setComprobantePreview] = useState(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [newTag, setNewTag] = useState("");

    const frequencyOptions = [
        { value: "semanal", label: "Semanal" },
        { value: "quincenal", label: "Quincenal" },
        { value: "mensual", label: "Mensual" },
        { value: "trimestral", label: "Trimestral" },
        { value: "semestral", label: "Semestral" },
        { value: "anual", label: "Anual" },
    ];

    const paymentMethods = [
        "Transferencia Bancaria",
        "Efectivo",
        "Cheque",
        "PayPal",
        "Tarjeta de Crédito",
        "Criptomonedas",
        "Otros",
    ];

    const incomeTypes = [
        { value: "unico", label: "Pago Único" },
        { value: "recurrente", label: "Ingreso Recurrente" },
        { value: "por_horas", label: "Por Horas Trabajadas" },
    ];

    // Verificar autenticación
    useEffect(() => {
        if (!user) {
            navigate("/login");
        }
    }, [user, navigate]);

    // Cargar datos
    useEffect(() => {
        if (user) {
            loadCategories();
        }
    }, [user]);

    // Cargar datos de ingreso si es edición
    useEffect(() => {
        if (user && isEditing && id) {
            loadIncomeData();
        }
    }, [user, isEditing, id]);

    const loadCategories = async () => {
        if (!user) return;

        setLoadingCategories(true);
        try {
            const categoriesRef = collection(db, "incomes", user.uid, "incomeCategories");
            const q = query(categoriesRef, orderBy("nombre", "asc"));
            const querySnapshot = await getDocs(q);

            const categoriesData = [];
            querySnapshot.forEach((doc) => {
                categoriesData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setCategories(categoriesData);
        } catch (error) {
            console.error("Error al cargar categorías:", error);
            setMessage({ text: "Error al cargar las categorías", type: "error" });
        } finally {
            setLoadingCategories(false);
        }
    };

    const loadIncomeData = async () => {
        if (!user || !id) return;

        try {
            const incomeRef = doc(db, "incomes", user.uid, "userIncomes", id);
            const incomeSnap = await getDoc(incomeRef);

            if (incomeSnap.exists()) {
                const data = incomeSnap.data();
                setFormData({
                    ...data,
                    fecha: data.fecha || "",
                    fechaFin: data.fechaFin || "",
                    cantidad: data.cantidad ? data.cantidad.toString() : "",
                    etiquetas: data.etiquetas || [],
                    comprobanteUrl: data.comprobanteUrl || "",
                    horasTrabajadas: data.horasTrabajadas ? data.horasTrabajadas.toString() : "",
                    tarifaPorHora: data.tarifaPorHora ? data.tarifaPorHora.toString() : "",
                });

                if (data.comprobanteUrl) {
                    setComprobantePreview(data.comprobanteUrl);
                }
            } else {
                setMessage({ text: "Ingreso no encontrado", type: "error" });
                navigate("/incomes");
            }
        } catch (error) {
            console.error("Error al cargar ingreso:", error);
            setMessage({ text: "Error al cargar los datos", type: "error" });
        }
    };

    const uploadFile = async (file) => {
        if (!file || !user) return null;

        setUploadingFile(true);
        try {
            const timestamp = Date.now();
            const fileExtension = file.name.split('.').pop();
            const fileName = `comprobante_${timestamp}.${fileExtension}`;
            const fileRef = ref(storage, `incomes/${user.uid}/comprobantes/${fileName}`);

            const snapshot = await uploadBytes(fileRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            return downloadURL;
        } catch (error) {
            console.error("Error al subir archivo:", error);
            setMessage({ text: "Error al subir el comprobante", type: "error" });
            return null;
        } finally {
            setUploadingFile(false);
        }
    };

    const deleteFile = async (fileUrl) => {
        if (!fileUrl) return;

        try {
            const fileRef = ref(storage, fileUrl);
            await deleteObject(fileRef);
        } catch (error) {
            console.error("Error al eliminar archivo:", error);
        }
    };

    const calculateTotalAmount = () => {
        if (formData.tipoIngreso === "por_horas" && formData.horasTrabajadas && formData.tarifaPorHora) {
            return parseFloat(formData.horasTrabajadas) * parseFloat(formData.tarifaPorHora);
        }
        return parseFloat(formData.cantidad) || 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            setMessage({ text: "Usuario no autenticado", type: "error" });
            return;
        }

        setLoading(true);

        try {
            let comprobanteUrl = formData.comprobanteUrl;

            if (comprobanteFile) {
                if (isEditing && formData.comprobanteUrl) {
                    await deleteFile(formData.comprobanteUrl);
                }

                comprobanteUrl = await uploadFile(comprobanteFile);
                if (!comprobanteUrl) {
                    setLoading(false);
                    return;
                }
            }

            const finalAmount = formData.tipoIngreso === "por_horas" ? calculateTotalAmount() : parseFloat(formData.cantidad);

            const incomeData = {
                ...formData,
                cantidad: finalAmount,
                comprobanteUrl: comprobanteUrl,
                horasTrabajadas: formData.horasTrabajadas ? parseFloat(formData.horasTrabajadas) : null,
                tarifaPorHora: formData.tarifaPorHora ? parseFloat(formData.tarifaPorHora) : null,
                updatedAt: new Date(),
                userId: user.uid,
            };

            if (isEditing) {
                const incomeRef = doc(db, "incomes", user.uid, "userIncomes", id);
                await updateDoc(incomeRef, incomeData);
                setMessage({ text: "Ingreso actualizado correctamente", type: "success" });
            } else {
                const incomesRef = collection(db, "incomes", user.uid, "userIncomes");
                await addDoc(incomesRef, {
                    ...incomeData,
                    createdAt: new Date(),
                });
                setMessage({ text: "Ingreso registrado correctamente", type: "success" });
            }

            setTimeout(() => {
                navigate("/incomes");
            }, 1500);
        } catch (error) {
            console.error("Error al guardar ingreso:", error);
            setMessage({ text: "Error al guardar el ingreso", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const addTag = () => {
        if (newTag.trim() && !formData.etiquetas.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                etiquetas: [...prev.etiquetas, newTag.trim()]
            }));
            setNewTag("");
        }
    };

    const removeTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            etiquetas: prev.etiquetas.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
            if (!validTypes.includes(file.type)) {
                setMessage({ text: "Tipo de archivo no válido. Solo se permiten imágenes (JPG, PNG, GIF) y PDF", type: "error" });
                return;
            }

            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                setMessage({ text: "El archivo es muy grande. Tamaño máximo: 5MB", type: "error" });
                return;
            }

            setComprobanteFile(file);

            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setComprobantePreview(e.target.result);
                };
                reader.readAsDataURL(file);
            } else {
                setComprobantePreview('pdf');
            }

            setMessage({ text: "Archivo seleccionado correctamente", type: "success" });
        }
    };

    const removeFile = () => {
        setComprobanteFile(null);
        setComprobantePreview(null);
        setFormData(prev => ({ ...prev, comprobanteUrl: "" }));

        const fileInput = document.getElementById('comprobante');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <Building2 className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Acceso no autorizado</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate("/incomes")}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <ChevronLeft className="w-6 h-6 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900">
                                    {isEditing ? "Editar Ingreso" : "Registrar Ingreso"}
                                </h1>
                                <p className="text-slate-600 font-medium">
                                    {isEditing
                                        ? "Modifique la información de su ingreso"
                                        : "Registre un nuevo ingreso en sus finanzas"
                                    }
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-900 to-blue-800 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">X</span>
                            </div>
                            <span className="text-xl font-bold text-blue-900">XFinance</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Form Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <div className="p-8">
                            {/* Información Básica */}
                            <div className="mb-8">
                                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                        <TrendingUp className="w-5 h-5 text-green-600" />
                                    </div>
                                    Información del Ingreso
                                </h2>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Descripción */}
                                    <div className="lg:col-span-2">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Descripción del Ingreso *
                                        </label>
                                        <input
                                            type="text"
                                            name="descripcion"
                                            value={formData.descripcion}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                            placeholder="Ej: Salario enero, Proyecto freelance, Venta productos..."
                                            required
                                        />
                                    </div>

                                    {/* Tipo de Ingreso */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Tipo de Ingreso *
                                        </label>
                                        <select
                                            name="tipoIngreso"
                                            value={formData.tipoIngreso}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                            required
                                        >
                                            {incomeTypes.map(type => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Fecha */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Fecha del Ingreso *
                                        </label>
                                        <div className="relative">
                                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="date"
                                                name="fecha"
                                                value={formData.fecha}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Monto o Cálculo por Horas */}
                                    {formData.tipoIngreso === "por_horas" ? (
                                        <>
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                    Horas Trabajadas *
                                                </label>
                                                <div className="relative">
                                                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                    <input
                                                        type="number"
                                                        name="horasTrabajadas"
                                                        step="0.5"
                                                        min="0"
                                                        value={formData.horasTrabajadas}
                                                        onChange={handleInputChange}
                                                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                        placeholder="8.5"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                    Tarifa por Hora *
                                                </label>
                                                <div className="relative">
                                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                    <input
                                                        type="number"
                                                        name="tarifaPorHora"
                                                        step="0.01"
                                                        min="0"
                                                        value={formData.tarifaPorHora}
                                                        onChange={handleInputChange}
                                                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                        placeholder="500.00"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            {formData.horasTrabajadas && formData.tarifaPorHora && (
                                                <div className="lg:col-span-2">
                                                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                                        <p className="text-green-800 font-semibold">
                                                            Total calculado: ${calculateTotalAmount().toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                                        </p>
                                                        <p className="text-sm text-green-600">
                                                            {formData.horasTrabajadas} horas × ${parseFloat(formData.tarifaPorHora).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                Monto *
                                            </label>
                                            <div className="relative">
                                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                <input
                                                    type="number"
                                                    name="cantidad"
                                                    step="0.01"
                                                    min="0"
                                                    value={formData.cantidad}
                                                    onChange={handleInputChange}
                                                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                    placeholder="0.00"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Categoría */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Categoría *
                                        </label>
                                        <div className="flex gap-2">
                                            <select
                                                name="categoria"
                                                value={formData.categoria}
                                                onChange={handleInputChange}
                                                className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                required
                                                disabled={loadingCategories}
                                            >
                                                <option value="">
                                                    {loadingCategories ? "Cargando..." : "Seleccionar categoría"}
                                                </option>
                                                {categories.map(category => (
                                                    <option key={category.id} value={category.nombre}>
                                                        {category.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => navigate("/income-categories")}
                                                className="px-4 py-3 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors flex items-center gap-2 font-medium"
                                                title="Gestionar categorías"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Información del Cliente/Proyecto */}
                            <div className="mb-8">
                                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <Users className="w-5 h-5 text-blue-600" />
                                    </div>
                                    Información del Cliente/Proyecto
                                </h2>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Fuente */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Fuente del Ingreso
                                        </label>
                                        <div className="relative">
                                            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="text"
                                                name="fuente"
                                                value={formData.fuente}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                placeholder="Ej: Empresa ABC, Cliente XYZ..."
                                            />
                                        </div>
                                    </div>

                                    {/* Cliente */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Cliente
                                        </label>
                                        <div className="relative">
                                            <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="text"
                                                name="cliente"
                                                value={formData.cliente}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                placeholder="Nombre del cliente"
                                            />
                                        </div>
                                    </div>

                                    {/* Proyecto */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Proyecto
                                        </label>
                                        <input
                                            type="text"
                                            name="proyecto"
                                            value={formData.proyecto}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                            placeholder="Nombre del proyecto"
                                        />
                                    </div>

                                    {/* Método de Cobro */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Método de Cobro
                                        </label>
                                        <select
                                            name="metodoCobro"
                                            value={formData.metodoCobro}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                        >
                                            <option value="">Seleccionar método</option>
                                            {paymentMethods.map(method => (
                                                <option key={method} value={method}>
                                                    {method}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Información Adicional */}
                            <div className="mb-8">
                                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <MapPin className="w-5 h-5 text-purple-600" />
                                    </div>
                                    Detalles Adicionales
                                </h2>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Ubicación */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Ubicación
                                        </label>
                                        <div className="relative">
                                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="text"
                                                name="ubicacion"
                                                value={formData.ubicacion}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                placeholder="Oficina, remoto, cliente..."
                                            />
                                        </div>
                                    </div>

                                    {/* Comprobante */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Comprobante
                                        </label>
                                        <div className="space-y-3">
                                            <div className="relative">
                                                <input
                                                    type="file"
                                                    accept="image/*,.pdf"
                                                    onChange={handleFileUpload}
                                                    className="hidden"
                                                    id="comprobante"
                                                    disabled={uploadingFile}
                                                />
                                                <label
                                                    htmlFor="comprobante"
                                                    className={`w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium bg-white cursor-pointer flex items-center gap-2 hover:bg-slate-50 ${uploadingFile ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                >
                                                    {uploadingFile ? (
                                                        <>
                                                            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                                                            Subiendo archivo...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Camera className="w-5 h-5 text-slate-400" />
                                                            {comprobanteFile || comprobantePreview
                                                                ? "Cambiar comprobante"
                                                                : "Adjuntar comprobante (JPG, PNG, PDF)"
                                                            }
                                                        </>
                                                    )}
                                                </label>
                                            </div>

                                            {/* Preview del comprobante */}
                                            {comprobantePreview && (
                                                <div className="relative bg-slate-50 rounded-lg p-4 border border-slate-200">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-center gap-3">
                                                            {comprobantePreview === 'pdf' ? (
                                                                <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center">
                                                                    <FileText className="w-8 h-8 text-red-600" />
                                                                </div>
                                                            ) : (
                                                                <img
                                                                    src={comprobantePreview}
                                                                    alt="Preview del comprobante"
                                                                    className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                                                                />
                                                            )}
                                                            <div>
                                                                <p className="font-medium text-slate-900">
                                                                    {comprobanteFile ? comprobanteFile.name : "Comprobante actual"}
                                                                </p>
                                                                {comprobanteFile && (
                                                                    <p className="text-sm text-slate-500">
                                                                        {(comprobanteFile.size / 1024).toFixed(1)} KB
                                                                    </p>
                                                                )}
                                                                <p className="text-xs text-slate-400 mt-1">
                                                                    {comprobantePreview === 'pdf' ? 'Documento PDF' : 'Imagen'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={removeFile}
                                                            className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                                                            title="Eliminar archivo"
                                                        >
                                                            <X className="w-5 h-5" />
                                                        </button>
                                                    </div>

                                                    {/* Vista completa para imágenes */}
                                                    {comprobantePreview && comprobantePreview !== 'pdf' && (
                                                        <div className="mt-3">
                                                            <img
                                                                src={comprobantePreview}
                                                                alt="Vista completa del comprobante"
                                                                className="w-full max-h-64 object-contain rounded-lg border border-slate-200 bg-white"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Etiquetas */}
                                    <div className="lg:col-span-2">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Etiquetas
                                        </label>
                                        <div className="space-y-3">
                                            <div className="flex gap-2">
                                                <div className="relative flex-1">
                                                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                    <input
                                                        type="text"
                                                        value={newTag}
                                                        onChange={(e) => setNewTag(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                                                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                        placeholder="Agregar etiqueta..."
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={addTag}
                                                    className="px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
                                                >
                                                    <Plus className="w-4 h-4" />
                                                </button>
                                            </div>
                                            {formData.etiquetas.length > 0 && (
                                                <div className="flex flex-wrap gap-2">
                                                    {formData.etiquetas.map((tag, index) => (
                                                        <span
                                                            key={index}
                                                            className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium"
                                                        >
                                                            {tag}
                                                            <button
                                                                type="button"
                                                                onClick={() => removeTag(tag)}
                                                                className="hover:bg-green-200 rounded-full p-0.5 transition-colors"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Notas */}
                                    <div className="lg:col-span-2">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Notas Adicionales
                                        </label>
                                        <div className="relative">
                                            <FileText className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                                            <textarea
                                                name="notas"
                                                value={formData.notas}
                                                onChange={handleInputChange}
                                                rows={4}
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                placeholder="Información adicional sobre el ingreso..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Ingreso Recurrente */}
                            <div className="mb-8">
                                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                                        <Calendar className="w-5 h-5 text-orange-600" />
                                    </div>
                                    Configuración de Recurrencia
                                </h2>

                                <div className="space-y-6">
                                    {/* Checkbox Recurrente */}
                                    <div className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            id="esRecurrente"
                                            name="esRecurrente"
                                            checked={formData.esRecurrente}
                                            onChange={handleInputChange}
                                            className="h-5 w-5 text-green-600 focus:ring-green-500 border-slate-300 rounded"
                                        />
                                        <label htmlFor="esRecurrente" className="text-sm font-semibold text-slate-700">
                                            Este es un ingreso recurrente
                                        </label>
                                    </div>

                                    {formData.esRecurrente && (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pl-8 border-l-2 border-green-200">
                                            {/* Frecuencia */}
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                    Frecuencia
                                                </label>
                                                <select
                                                    name="frecuenciaRecurrencia"
                                                    value={formData.frecuenciaRecurrencia}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                >
                                                    {frequencyOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Fecha Fin */}
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                    Fecha de Fin (Opcional)
                                                </label>
                                                <input
                                                    type="date"
                                                    name="fechaFin"
                                                    value={formData.fechaFin}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all font-medium"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Buttons */}
                            <div className="flex gap-4 pt-6 border-t border-slate-200">
                                <button
                                    type="button"
                                    onClick={() => navigate("/incomes")}
                                    className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || uploadingFile || !formData.descripcion || !formData.categoria || (!formData.cantidad && formData.tipoIngreso !== "por_horas") || (formData.tipoIngreso === "por_horas" && (!formData.horasTrabajadas || !formData.tarifaPorHora))}
                                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {loading || uploadingFile ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            {uploadingFile ? "Subiendo archivo..." : isEditing ? "Actualizando..." : "Guardando..."}
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            {isEditing ? "Actualizar Ingreso" : "Registrar Ingreso"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* Toast Message */}
            {message.text && (
                <div
                    className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg transition-all duration-300 z-50 max-w-md ${message.type === "error"
                            ? "bg-red-50 border border-red-200 text-red-800"
                            : "bg-green-50 border border-green-200 text-green-800"
                        }`}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {message.type === "error" ? (
                                <AlertCircle className="w-5 h-5 text-red-600" />
                            ) : (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            )}
                            <span className="font-semibold">{message.text}</span>
                        </div>
                        <button
                            onClick={() => setMessage({ text: "", type: "" })}
                            className="ml-4 p-1 hover:bg-white/50 rounded-full transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default XFinanceIncomeForm;